import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Checkbox, FormControlLabel, Grid } from '@mui/material';
import { FC, useState } from 'react';
import { IdentityCheckFlowDevelopment } from '~/api/types';
import { DevelopmentCheckboxButton } from '~/components/button/development-checkbox-button';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';

const ALREADY_OPTED_IN_BADGE_LABEL = 'Notificação já ativa';

interface NotificationOptInSelectionScreenProps {
  /**
   * The development the customer is validating their identity for. It is always
   * opted in, so it is only shown as the reference for the other ones.
   */
  selectedDevelopment: IdentityCheckFlowDevelopment;
  /**
   * The other developments of the legal person notified by the same WhatsApp
   * number as the conversation the validation was started from. Only those can
   * be opted in at once, since the customer is identified by the same bsuid in
   * all of them.
   */
  developments: IdentityCheckFlowDevelopment[];
  /**
   * The developments among them whose notifications are already active. They are
   * shown checked and locked, and are left out of the confirmed selection.
   */
  alreadyOptedInDevelopmentIds: number[];
  onConfirm: (selectedDevelopmentIds: number[]) => void;
  goBack: () => void;
}

export const NotificationOptInSelectionScreen: FC<NotificationOptInSelectionScreenProps> = ({
  selectedDevelopment,
  developments,
  alreadyOptedInDevelopmentIds,
  onConfirm,
  goBack,
}) => {
  const isAlreadyOptedIn = (developmentId: number) => alreadyOptedInDevelopmentIds.includes(developmentId);

  // the ones already opted in cannot be toggled: there is nothing left to ask
  // for them
  const toggleableDevelopments = developments.filter((d) => !isAlreadyOptedIn(d.id));

  // all of them come selected, so receiving the boletos of every development
  // notified by this conversation is the default
  const [selectedDevelopmentIds, setSelectedDevelopmentIds] = useState<number[]>(
    toggleableDevelopments.map((d) => d.id),
  );

  const allSelected =
    toggleableDevelopments.length > 0 && selectedDevelopmentIds.length === toggleableDevelopments.length;

  const toggleDevelopment = (developmentId: number) => {
    setSelectedDevelopmentIds((prev) =>
      prev.includes(developmentId) ? prev.filter((id) => id !== developmentId) : [...prev, developmentId],
    );
  };

  const toggleAll = () => {
    setSelectedDevelopmentIds(allSelected ? [] : toggleableDevelopments.map((d) => d.id));
  };

  const description =
    toggleableDevelopments.length === 0
      ? `Além do ${selectedDevelopment.name}, você tem contrato nos empreendimentos abaixo, que já enviam seus boletos por este mesmo WhatsApp.`
      : `Além do ${selectedDevelopment.name}, você tem contrato nos empreendimentos abaixo, que enviam as notificações por este mesmo WhatsApp. Desmarque os que não devem enviar seus boletos neste WhatsApp.`;

  return (
    <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
      <Title>Quer receber os boletos de outros empreendimentos?</Title>
      <Box width={{ xs: '100%' }}>
        <Body1>{description}</Body1>
      </Box>
      {toggleableDevelopments.length > 1 && (
        <Box sx={{ width: 'fit-content', mx: 'auto', mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={allSelected} onChange={toggleAll} />}
            label="Selecionar todos"
          />
        </Box>
      )}
      <Box>
        {developments.map((development) => {
          const alreadyOptedIn = isAlreadyOptedIn(development.id);

          return (
            <DevelopmentCheckboxButton
              key={development.id}
              development={development}
              // a development already opted in reads as checked, since its boletos
              // do come to this WhatsApp, but it cannot be turned off from here
              checked={alreadyOptedIn || selectedDevelopmentIds.includes(development.id)}
              onToggle={() => toggleDevelopment(development.id)}
              disabled={alreadyOptedIn}
              badgeLabel={alreadyOptedIn ? ALREADY_OPTED_IN_BADGE_LABEL : undefined}
            />
          );
        })}
      </Box>
      <Grid container>
        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }} sx={{ textAlign: { xs: 'center', md: 'left' }, mt: 2 }}>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Grid>
        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'right' }, mt: 2 }}>
          <Button
            onClick={() => onConfirm(selectedDevelopmentIds)}
            variant="contained"
            fullWidth
            sx={{ fontWeight: 'bold' }}
          >
            Continuar
          </Button>
        </Grid>
      </Grid>
    </Page>
  );
};
